@use "foundation/colors";
@use "foundation/responsive";

// Stripe express buttons default to 44px tall; maxRows: 1 keeps up to three wallets on one line.
$express-button-height: 44px;
$express-button-gap: 8px;
$express-row-reserve: $express-button-height;

.payment-info-form {
  margin: 0 auto;
  max-width: 100%;
  width: 100%;

  @include responsive.phone {
    width: 100%;
  }

  .payment-element-slot {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    // Fixed-px reserves keep the placeholder height stable across viewport heights — a `vh`-based
    // clamp varies the reserve per viewport and contributed secondary shift on resize.
    min-height: 232px;

    @include responsive.phone {
      min-height: 220px;
    }

    @include responsive.tablet-and-up {
      min-height: 208px;
    }
  }

  :global(.or-divider) {
    color: colors.$neutrals-grey;
    margin-top: 5px;
    margin-bottom: 30px;

    &::before,
    &::after {
      border-bottom-color: colors.$neutrals-light-grey;
    }
  }

  .checkout-form-layout {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  &[data-express-checkout-placement="below_cta"] .checkout-form-layout {
    .express-checkout-region {
      order: 3;
    }

    .payment-element-slot {
      order: 0;
    }

    .action-buttons:not(.action-buttons--fineprint-only) {
      order: 1;
    }

    .action-buttons--fineprint-only {
      order: 4;
    }
  }

  &[data-express-checkout-placement="above"] .checkout-form-layout,
  &:not([data-express-checkout-placement="below_cta"]) .checkout-form-layout {
    .express-checkout-region {
      order: 0;
    }

    .payment-element-slot {
      order: 1;
    }

    .action-buttons:not(.action-buttons--fineprint-only) {
      order: 2;
    }
  }

  // Wallet-below variant: extra separation between card CTA and express checkout divider (~5× default gap).
  &[data-express-checkout-placement="below_cta"] .express-checkout-region :global(.or-divider) {
    margin-top: 28px;
  }

  // Accordion + radios: more space above card CTA (~3.5× default action-buttons margin).
  &[data-payment-element-layout="accordion_radios"] .action-buttons:not(.action-buttons--fineprint-only) {
    margin-top: 168px;

    @include responsive.phone {
      margin-top: 112px;
    }

    @media (min-width: 724px) and (max-width: 939px) {
      margin-top: 126px;
    }

    @media (min-width: 940px) {
      margin-top: 28px;
    }
  }

  // Card-only: no bank tab — halve default gap above card CTA.
  &[data-restrict-to-card-only="true"] .action-buttons:not(.action-buttons--fineprint-only) {
    margin-top: 24px;

    @include responsive.phone {
      margin-top: 16px;
    }

    @media (min-width: 724px) and (max-width: 939px) {
      margin-top: 18px;
    }

    @media (min-width: 940px) {
      margin-top: 4px;
    }
  }

  // Accordion without radios: extra clearance above card CTA (+16px vs default).
  &[data-payment-element-layout="accordion_no_radios"] .action-buttons:not(.action-buttons--fineprint-only) {
    margin-top: 64px;

    @include responsive.phone {
      margin-top: 48px;
    }

    @media (min-width: 724px) and (max-width: 939px) {
      margin-top: 52px;
    }

    @media (min-width: 940px) {
      margin-top: 24px;
    }
  }

  // V4 mobile: Stripe accordion without radios needs taller reserve so fields/CTA are not clipped.
  &[data-payment-element-layout="accordion_no_radios"] .payment-element-slot {
    min-height: 280px;
    overflow: visible;

    @include responsive.phone {
      min-height: 300px;
    }
  }

  .express-checkout-slot {
    --express-checkout-row-height: #{$express-row-reserve};
    box-sizing: border-box;
    height: var(--express-checkout-row-height);
    margin-bottom: 20px;
    max-height: var(--express-checkout-row-height);
    min-height: var(--express-checkout-row-height);
    overflow: hidden;
    width: 100%;
  }

  // ab_express=always: wallets can wrap (maxRows:0); grow slot so Link row does not overlap "or".
  .express-checkout-slot--always-wallets {
    height: auto;
    max-height: none;
    min-height: var(--express-checkout-row-height);
    overflow: visible;

    .express-checkout-inner,
    .express-checkout-mount {
      height: auto;
      min-height: var(--express-checkout-row-height);
      overflow: visible;
    }
  }

  .express-checkout-inner {
    height: 100%;
    line-height: 0;
    position: relative;
    width: 100%;
  }

  .express-checkout-mount {
    font-size: 0;
    height: 100%;
    line-height: 0;
    overflow: hidden;
    transition: none;
    width: 100%;

    // Stripe may animate iframe appearance; keep the swap instant for CLS.
    &,
    :global(*) {
      animation: none !important;
      transition: none !important;
    }

    // Kill inline/iframe strut gap under wallet iframes (~3–4px) without changing button height.
    :global(iframe) {
      display: block;
      vertical-align: top;
    }
  }

  // Skeleton covers the slot visually; keep the Stripe mount visible for Apple Pay secure-context checks.
  .express-checkout-inner--loading .express-checkout-mount {
    pointer-events: none;
  }

  .express-checkout-skeleton-overlay {
    animation: none;
    inset: 0;
    pointer-events: none;
    position: absolute;
    transition: none;
    z-index: 1;
  }

  .express-checkout-inner--loading .express-checkout-skeleton-overlay {
    opacity: 1;
    visibility: visible;
  }

  .express-checkout-inner:not(.express-checkout-inner--loading) .express-checkout-skeleton-overlay {
    opacity: 0;
    visibility: hidden;
  }

  .express-checkout-skeleton-bar {
    animation: none;
    background-color: rgba(colors.$primary-eerie-black, 0.08);
    border-radius: 8px;
    height: 100%;
    transition: none;
    width: 100%;
  }

  // Keeps Stripe’s iframe intrinsic min-content from widening the checkout column when Elements mounts.
  .stripe-mount {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;

    :global(.StripeElement) {
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }

    // Stripe wraps iframes in layered divs — cap width without touching iframe internals.
    & > :global(div) {
      max-width: 100%;
      box-sizing: border-box;
    }
  }

  .payment-element-placeholder {
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
    // Card-field reserve only — wallet row + "or" live in express-checkout-slot above.
    min-height: clamp(168px, 16vh, 220px);
  }

  .skeleton-field {
    width: 100%;
    min-height: 52px;
    border-radius: 8px;
    background-color: rgba(colors.$primary-eerie-black, 0.08);
  }

  .skeleton-split-row {
    display: flex;
    gap: 8px;
    width: 100%;
  }

  .skeleton-field-half {
    flex: 1;
    min-height: 52px;
    border-radius: 8px;
    background-color: rgba(colors.$primary-eerie-black, 0.08);
  }

  .action-buttons {
    text-align: center;
    margin-top: 48px;

    // Keep CTA clear of fixed support widgets near the viewport bottom.
    @include responsive.phone {
      margin-top: 32px;
    }

    // Stripe Payment Element: observed ~3 rows below ~940px viewport (724–939px tablet band on
    // onboarding checkout), ~2 rows at 940px and up. Match CTA spacing to that (static at first
    // paint — no CLS from these margins alone).
    @media (min-width: 724px) and (max-width: 939px) {
      margin-top: 36px;
    }

    @media (min-width: 940px) {
      margin-top: 8px;
    }

    &--fineprint-only {
      margin-top: 16px;
    }
  }

  .status-copy {
    color: colors.$neutrals-dark-grey;
    font-size: 14px;
    font-weight: 600;
    margin-top: 12px;
    text-align: center;
  }

  .intent-error {
    color: colors.$neutrals-dark-grey;
    font-size: 14px;
    margin-top: 12px;
    text-align: center;

    p {
      margin-bottom: 8px;
    }
  }

  // Update payment: fill billing column without extra mandate block below Stripe.
  &.payment-info-form--billing-update {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;

    .payment-element-slot {
      flex: 0 0 auto;
      min-height: 0;
    }

    .action-buttons {
      flex-shrink: 0;
      margin-top: 24px;
    }
  }
}
